import React, {
  createContext,
  useCallback,
  useMemo,
  useState,
  ReactNode,
  useContext
} from "react";
import { InfoModal } from "../components/Shared/Modals/InfoModal/InfoModal";

export interface InfoModalAction {
  name: string;
  type: "default" | "primary" | "secondary";
  text: string;
}

export interface InfoModalOptions {
  title: string;
  children: ReactNode;
  actions?: InfoModalAction[];
  onAction?: (actionName: string) => void;
}

type InfoModalContextType = {
  showInfoModal: (options: InfoModalOptions) => void;
  hideInfoModal: () => void;
};

export const InfoModalContext = createContext<InfoModalContextType>({
  showInfoModal: () => {},
  hideInfoModal: () => {}
});

export const InfoModalContextProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    options: InfoModalOptions | null;
  }>({
    isOpen: false,
    options: null
  });

  const showInfoModal = useCallback((options: InfoModalOptions) => {
    setModalState({
      isOpen: true,
      options
    });
  }, []);

  const hideInfoModal = useCallback(() => {
    setModalState({
      isOpen: false,
      options: null
    });
  }, []);

  const handleAction = useCallback(
    (actionName: string) => {
      if (modalState.options?.onAction) {
        modalState.options.onAction(actionName);
      }
      hideInfoModal();
    },
    [modalState.options, hideInfoModal]
  );

  const contextValue = useMemo(
    () => ({
      showInfoModal,
      hideInfoModal
    }),
    [showInfoModal, hideInfoModal]
  );

  return (
    <InfoModalContext.Provider value={contextValue}>
      {children}
      {modalState.isOpen && modalState.options && (
        <InfoModal
          isOpen={modalState.isOpen}
          title={modalState.options.title}
          actions={modalState.options.actions}
          onAction={handleAction}
        >
          {modalState.options.children}
        </InfoModal>
      )}
    </InfoModalContext.Provider>
  );
};

export const useInfoModal = () => {
  const context = useContext(InfoModalContext);
  if (!context) {
    throw new Error(
      "useInfoModal must be used within an InfoModalContextProvider"
    );
  }
  return context;
};
